import { newBinding, matches } from '@boba-cli/key';
import { Style } from '@boba-cli/chapstick';
import { msg, KeyMsg } from '@boba-cli/tea';

// src/fs.ts
async function readDirectory(filesystem, pathAdapter, path, showHidden, dirFirst = true) {
  const result = await filesystem.readdir(path, { withFileTypes: true });
  if (typeof result[0] === "string") {
    throw new Error("Expected DirectoryEntry array but got string array");
  }
  const entries = result;
  const files = [];
  for (const entry of entries) {
    const isHidden = isHiddenUnix(entry.name);
    if (!showHidden && isHidden) continue;
    const fullPath = pathAdapter.join(path, entry.name);
    const stats = await filesystem.stat(fullPath);
    files.push({
      name: entry.name,
      path: fullPath,
      isDir: entry.isDirectory(),
      isHidden,
      size: stats.size,
      mode: stats.mode
    });
  }
  return files.sort((a, b) => sortFiles(a, b, dirFirst));
}
function sortFiles(a, b, dirFirst = true) {
  if (dirFirst) {
    if (a.isDir && !b.isDir) return -1;
    if (!a.isDir && b.isDir) return 1;
  }
  return a.name.localeCompare(b.name);
}
function isHiddenUnix(name) {
  return name.startsWith(".");
}
var defaultKeyMap = {
  up: newBinding({ keys: ["up", "k"] }),
  down: newBinding({ keys: ["down", "j"] }),
  select: newBinding({ keys: ["enter"] }),
  back: newBinding({ keys: ["backspace", "h", "left"] }),
  open: newBinding({ keys: ["right", "l"] }),
  toggleHidden: newBinding({ keys: ["."] }),
  pageUp: newBinding({ keys: ["pgup", "u"] }),
  pageDown: newBinding({ keys: ["pgdown", "d"] }),
  gotoTop: newBinding({ keys: ["home", "g"] }),
  gotoBottom: newBinding({ keys: ["end", "G"] }),
  shortHelp() {
    return [this.up, this.down, this.select, this.back];
  },
  fullHelp() {
    return [
      [this.up, this.down, this.pageUp, this.pageDown],
      [this.select, this.open, this.back, this.toggleHidden],
      [this.gotoTop, this.gotoBottom]
    ];
  }
};

// src/messages.ts
var DirReadMsg = class {
  constructor(path, files, error) {
    this.path = path;
    this.files = files;
    this.error = error;
  }
  _tag = "filepicker-dir-read";
};
var FileSelectedMsg = class {
  constructor(file) {
    this.file = file;
  }
  _tag = "filepicker-file-selected";
};
function defaultStyles() {
  return {
    directory: new Style().bold(true),
    file: new Style(),
    hidden: new Style().italic(true),
    selected: new Style().background("#303030").foreground("#ffffff"),
    cursor: new Style().bold(true),
    status: new Style().italic(true)
  };
}
function filterByType(files, allowed) {
  if (!allowed || allowed.length === 0) return files;
  return files.filter((f) => {
    if (f.isDir) return true;
    return allowed.some((ext) => f.name.endsWith(ext));
  });
}
var FilepickerModel = class _FilepickerModel {
  currentDir;
  files;
  cursor;
  selectedFile;
  showHidden;
  showPermissions;
  showSize;
  dirFirst;
  height;
  allowedTypes;
  styles;
  keyMap;
  filesystem;
  path;
  constructor(state) {
    this.currentDir = state.currentDir;
    this.files = state.files;
    this.cursor = state.cursor;
    this.selectedFile = state.selectedFile;
    this.showHidden = state.showHidden;
    this.showPermissions = state.showPermissions;
    this.showSize = state.showSize;
    this.dirFirst = state.dirFirst;
    this.height = state.height;
    this.allowedTypes = state.allowedTypes;
    this.styles = state.styles;
    this.keyMap = state.keyMap;
    this.filesystem = state.filesystem;
    this.path = state.path;
  }
  /** Create a new model and command to read the directory. */
  static new(options) {
    const styles = { ...defaultStyles(), ...options.styles ?? {} };
    const model = new _FilepickerModel({
      currentDir: options.currentDir ?? options.filesystem.cwd(),
      files: [],
      cursor: 0,
      selectedFile: null,
      showHidden: options.showHidden ?? false,
      showPermissions: options.showPermissions ?? false,
      showSize: options.showSize ?? false,
      dirFirst: options.dirFirst ?? true,
      height: options.height ?? 0,
      allowedTypes: options.allowedTypes ?? [],
      styles,
      keyMap: options.keyMap ?? defaultKeyMap,
      filesystem: options.filesystem,
      path: options.path
    });
    return model.refresh();
  }
  /** Current selected file (if any). */
  selected() {
    return this.files[this.cursor];
  }
  /** Go to the parent directory. */
  back() {
    const parent = this.path.dirname(this.currentDir);
    const model = this.with({ currentDir: parent, cursor: 0 });
    return model.refresh();
  }
  /** Enter the highlighted directory, or select a file. */
  enter() {
    const file = this.selected();
    if (!file) return [this, null];
    if (file.isDir) {
      const model = this.with({
        currentDir: file.path,
        cursor: 0,
        selectedFile: null
      });
      return model.refresh();
    }
    return this.select();
  }
  /** Refresh the current directory listing. */
  refresh() {
    const cmd = async () => {
      try {
        const files = await readDirectory(
          this.filesystem,
          this.path,
          this.currentDir,
          this.showHidden,
          this.dirFirst
        );
        const filtered = filterByType(files, this.allowedTypes);
        return new DirReadMsg(this.currentDir, filtered);
      } catch (error) {
        return new DirReadMsg(this.currentDir, [], error);
      }
    };
    return [this, cmd];
  }
  /** Select the current file. */
  select() {
    const file = this.selected();
    if (!file) return [this, null];
    const model = this.with({ selectedFile: file });
    return [model, msg(new FileSelectedMsg(file))];
  }
  /** Move cursor up. */
  cursorUp() {
    if (this.files.length === 0) return this;
    const next = Math.max(0, this.cursor - 1);
    return this.with({ cursor: next });
  }
  /** Move cursor down. */
  cursorDown() {
    if (this.files.length === 0) return this;
    const next = Math.min(this.files.length - 1, this.cursor + 1);
    return this.with({ cursor: next });
  }
  /** Jump to first entry. */
  gotoTop() {
    if (this.files.length === 0) return this;
    return this.with({ cursor: 0 });
  }
  /** Jump to last entry. */
  gotoBottom() {
    if (this.files.length === 0) return this;
    return this.with({ cursor: this.files.length - 1 });
  }
  /** Toggle hidden file visibility. */
  toggleHidden() {
    const model = this.with({ showHidden: !this.showHidden, cursor: 0 });
    return model.refresh();
  }
  /** Tea init hook; triggers directory read. */
  init() {
    const [, cmd] = this.refresh();
    return cmd;
  }
  /** Tea update handler. */
  update(msgObj) {
    if (msgObj instanceof DirReadMsg) {
      if (msgObj.error) {
        return [
          this.with({
            files: [],
            cursor: 0,
            selectedFile: null
          }),
          null
        ];
      }
      const files = msgObj.files;
      const cursor = Math.min(this.cursor, Math.max(0, files.length - 1));
      return [
        this.with({
          currentDir: msgObj.path,
          files,
          cursor,
          selectedFile: files[cursor] ?? null
        }),
        null
      ];
    }
    if (msgObj instanceof KeyMsg) {
      if (matches(msgObj, this.keyMap.up)) return [this.cursorUp(), null];
      if (matches(msgObj, this.keyMap.down)) return [this.cursorDown(), null];
      if (matches(msgObj, this.keyMap.gotoTop)) return [this.gotoTop(), null];
      if (matches(msgObj, this.keyMap.gotoBottom))
        return [this.gotoBottom(), null];
      if (matches(msgObj, this.keyMap.toggleHidden)) return this.toggleHidden();
      if (matches(msgObj, this.keyMap.back)) return this.back();
      if (matches(msgObj, this.keyMap.open)) return this.enter();
      if (matches(msgObj, this.keyMap.select)) return this.select();
    }
    return [this, null];
  }
  /** Render the file list. */
  view() {
    const lines = [];
    const header = this.styles.status.render(this.currentDir);
    lines.push(header);
    if (this.files.length === 0) {
      lines.push(this.styles.status.render("(empty)"));
      return lines.join("\n");
    }
    for (const [index, file] of this.files.entries()) {
      const isSelected = index === this.cursor;
      const style = file.isDir ? this.styles.directory : this.styles.file;
      const base = file.isHidden ? this.styles.hidden : style;
      const name = base.render(file.name);
      const cursor = isSelected ? this.styles.cursor.render("\u27A4 ") : "  ";
      const line = isSelected ? this.styles.selected.render(cursor + name) : cursor + name;
      lines.push(line);
    }
    return lines.join("\n");
  }
  with(patch) {
    return new _FilepickerModel({
      currentDir: this.currentDir,
      files: this.files,
      cursor: this.cursor,
      selectedFile: this.selectedFile,
      showHidden: this.showHidden,
      showPermissions: this.showPermissions,
      showSize: this.showSize,
      dirFirst: this.dirFirst,
      height: this.height,
      allowedTypes: this.allowedTypes,
      styles: this.styles,
      keyMap: this.keyMap,
      filesystem: this.filesystem,
      path: this.path,
      ...patch
    });
  }
};

export { DirReadMsg, FileSelectedMsg, FilepickerModel, defaultKeyMap, isHiddenUnix, readDirectory, sortFiles };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map